import { Fragment, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Clock, X } from 'lucide-react';
import { addRecentSearch, removeRecentSearch, useRecentSearches } from '../lib/recent';
import { usePlayer } from '../player/store';
import { Cover } from '../components/Cover';
import { useGenres, useSearch } from '../api/hooks';
import { AlbumCard, Grid, PersonCard, Shelf } from '../components/Cards';
import { SeriesCard } from './Stories';
import { useAccess } from '../lib/access';
import { Empty, Loading } from '../components/States';
import { TrackList } from '../components/TrackList';
import { hashHue } from '../lib/format';

export function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get('q') ?? '';
  const res = useSearch(q);
  const { hasMusic } = useAccess();
  const genres = useGenres();
  const recent = useRecentSearches();
  const nav = useNavigate();
  const playTracks = usePlayer((s) => s.playTracks);
  const d0 = res.data;
  const found = !!d0 && (d0.tracks.length + d0.albums.length + d0.artists.length + d0.composers.length + d0.series.length + (d0.lyrics?.length ?? 0) > 0);
  // Gardée dans les recherches récentes si on reste un moment sur des résultats
  useEffect(() => {
    if (!q.trim() || !found) return;
    const t = window.setTimeout(() => addRecentSearch(q), 1500);
    return () => window.clearTimeout(t);
  }, [q, found]);

  const recentBlock = recent.length > 0 && (
    <>
      <h2 className="section__title">Recherches récentes</h2>
      <div className="recent">
        {recent.map((r) => (
          <span key={r} className="chip" onClick={() => nav(`/search?q=${encodeURIComponent(r)}`)}>
            <Clock size={14} aria-hidden /> {r}
            <button
              className="icon-btn"
              style={{ width: '1.3rem', height: '1.3rem' }}
              onClick={(e) => {
                e.stopPropagation();
                removeRecentSearch(r);
              }}
              aria-label={`Retirer « ${r} »`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
    </>
  );

  if (!q.trim() && !hasMusic)
    return (
      <div className="page">
        <h1 className="page__title">Rechercher</h1>
        <p className="page__lead">Tapez le nom d’une histoire ou d’un épisode.</p>
        {recentBlock}
      </div>
    );
  if (!q.trim()) {
    return (
      <div className="page">
        {recentBlock}
        <h1 className="page__title">Parcourir les genres</h1>
        {genres.isLoading && <Loading />}
        <div className="genres">
          {genres.data?.map((g) => (
            <Link key={g.id} to={`/genre/${encodeURIComponent(g.id)}`} className="genre" style={{ ['--h' as string]: hashHue(g.name) }}>
              <span className="genre__name">{g.name}</span>
              {g.trackCount !== undefined && <span className="genre__count">{g.trackCount} titres</span>}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  const d = res.data;
  const nothing = d && !found;
  return (
    <div className="page">
      <h1 className="page__title page__title--small">Résultats pour « {d?.corrected ?? q} »</h1>
      {d?.corrected && <p className="search-fix">Aucun résultat pour « {q} » : voici ceux pour « {d.corrected} ».</p>}
      {res.isLoading && <Loading label="Recherche" />}
      {nothing && <Empty title="Aucun résultat">Vérifiez l’orthographe ou cherchez un nom de compositeur, d’album ou de titre.</Empty>}
      {d && d.tracks.length > 0 && (
        <section className="section">
          <h2 className="section__title">Titres</h2>
          <TrackList tracks={d.tracks.slice(0, 10)} showCover showAlbum source={`Recherche « ${q} »`} />
        </section>
      )}
      {d?.lyrics && d.lyrics.length > 0 && (
        <section className="section">
          <h2 className="section__title">Dans les paroles</h2>
          <ul className="lyrics-hits">
            {d.lyrics.map((h, i) => (
              <li key={h.track.id}>
                <button className="lyrics-hit" onClick={() => playTracks(d.lyrics!.map((x) => x.track), i, { source: `Paroles « ${q} »`, shuffle: false })}>
                  <Cover art={h.track.cover} name={h.track.title} size={64} />
                  <span>
                    <span className="lyrics-hit__title">{h.track.title}</span>
                    <span className="muted small"> · {h.track.artists.map((a) => a.name).join(', ')}</span>
                    <span className="lyrics-hit__excerpt">« <Excerpt text={h.excerpt} /> »</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {d && d.series.length > 0 && (
        <Shelf title="Histoires">
          {d.series.map((s) => (
            <SeriesCard key={s.id} series={s} />
          ))}
        </Shelf>
      )}
      {d && d.composers.length > 0 && (
        <Shelf title="Compositeurs">
          {d.composers.map((c) => (
            <PersonCard key={c.id} person={c} kind="composer" />
          ))}
        </Shelf>
      )}
      {d && d.artists.length > 0 && (
        <Shelf title="Artistes">
          {d.artists.map((a) => (
            <PersonCard key={a.id} person={a} kind="artist" />
          ))}
        </Shelf>
      )}
      {d && d.albums.length > 0 && (
        <section className="section">
          <h2 className="section__title">Albums</h2>
          <Grid>
            {d.albums.map((a) => (
              <AlbumCard key={a.id} album={a} />
            ))}
          </Grid>
        </section>
      )}
    </div>
  );
}

/** Extrait de paroles : les mots trouvés (entre \u0002 et \u0003) en surbrillance */
function Excerpt({ text }: { text: string }) {
  const parts = text.split(/(\u0002[^\u0003]*\u0003)/);
  return (
    <>
      {parts.map((p, i) => (p.startsWith('\u0002') ? <mark key={i}>{p.slice(1, -1)}</mark> : <Fragment key={i}>{p}</Fragment>))}
    </>
  );
}
